import React, { Component } from 'react';
import classNames from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Modal from '@mui/material/Modal';
import NoSsr from '@mui/material/NoSsr';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { injectIntl, defineMessages } from 'react-intl';
import { convertFromRaw, EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { Interweave } from 'interweave';
import moment from 'moment';
import isLoggedIn from '../../helpers/checkAuth';
import RestrictedAccessModal from '../RestrictedAccessModal/RestrictedAccessModal';
import s from './ApprovalStepListItem.scss';
import SkholeIconApprovalStep from '../statelessComponents/Icons/SkholeIconApprovalStep';
import { emptyContent } from '../RichEditor/RichEditor';
import APPROVAL_STATUSES from '../../constants/approvalStepStatuses';
import { buildQuery } from '../../helpers';
import AppContext from '../../context';
import * as courseDetailsActions from '../../actions/courseDetails';
export const getApprovalStepId = (item) => `approval-step-${item._id}`;
const messages = defineMessages({
    notAvailableOffline: {
        id: 'ApprovalStepListItem.notAvailableOffline',
        defaultMessage: 'Not available offline',
    },
    notAvailableInOfflineMode: {
        id: 'ApprovalStepListItem.notAvailableInOfflineMode',
        defaultMessage: 'Not available in offline mode',
    },
    archived: {
        id: 'ApprovalStepListItem.archived',
        defaultMessage: 'Archived',
    },
    missingApprover: {
        id: 'ApprovalStepListItem.missingApprover',
        defaultMessage: 'Missing approver',
    },
    instructions: {
        id: 'ApprovalStepListItem.instructions',
        defaultMessage: 'Instructions',
    },
    dueDate: {
        id: 'ApprovalStepListItem.dueDate',
        defaultMessage: 'Due date',
    },
    status: {
        id: 'ApprovalStepListItem.status',
        defaultMessage: 'Status',
    },
    notCompleted: {
        id: 'ApprovalStepListItem.notCompleted',
        defaultMessage: 'Not completed',
    },
    waitingForReview: {
        id: 'ApprovalStepListItem.waitingForReview',
        defaultMessage: 'Waiting for review',
    },
    approved: {
        id: 'ApprovalStepListItem.approved',
        defaultMessage: 'Approved',
    },
    completed: {
        id: 'ApprovalStepListItem.completed',
        defaultMessage: 'Completed',
    },
    rejected: {
        id: 'ApprovalStepListItem.rejected',
        defaultMessage: 'Rejected',
    },
    markAsCompleted: {
        id: 'ApprovalStepListItem.markAsCompleted',
        defaultMessage: 'Mark as completed',
    },
    markAsCompletedTooltip: {
        id: 'ApprovalStepListItem.markAsCompletedTooltip',
        defaultMessage: 'When you have completed the requirement, mark the step as completed to request teacher approval.',
    },
    confirmCompletion: {
        id: 'ApprovalStepListItem.confirmCompletion',
        defaultMessage: 'Confirm completion',
    },
    confirmCompletionDescription: {
        id: 'ApprovalStepListItem.confirmCompletionDescription',
        defaultMessage: 'By marking this step as completed, you confirm that you have fulfilled the requirement described above.',
    },
    completionError: {
        id: 'ApprovalStepListItem.completionError',
        defaultMessage: 'Could not mark approval step as completed.',
    },
    cancel: {
        id: 'ApprovalStepListItem.cancel',
        defaultMessage: 'Cancel',
    },
    confirm: {
        id: 'ApprovalStepListItem.confirm',
        defaultMessage: 'Confirm',
    },
});
const getStatusMessage = (status) => {
    switch (status) {
        case APPROVAL_STATUSES.WAITING_FOR_REVIEW:
            return 'waitingForReview';
        case APPROVAL_STATUSES.APPROVED:
            return 'approved';
        case APPROVAL_STATUSES.REJECTED:
            return 'rejected';
        default:
            return 'notCompleted';
    }
};
const getStatusClassName = (status) => {
    switch (status) {
        case APPROVAL_STATUSES.WAITING_FOR_REVIEW:
            return s.waitingForReviewStatus;
        case APPROVAL_STATUSES.APPROVED:
            return s.approvedStatus;
        case APPROVAL_STATUSES.REJECTED:
            return s.rejectedStatus;
        default:
            return s.notCompletedStatus;
    }
};
class ApprovalStepListItem extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            completionError: '',
            isRestrictedAccessModalOpened: false,
            isConfirmCompletionDialogOpened: false,
            isSubmittingCompletion: false,
            localStatus: this.props.status || APPROVAL_STATUSES.NOT_SUBMITTED,
        };
        this.hasAccess = () => {
            const { auth, course } = this.props;
            return isLoggedIn(auth) && course.isAvailable;
        };
        this.checkAuth = (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (this.hasAccess()) {
                this.openConfirmCompletionDialog();
                return;
            }
            this.setState({
                isRestrictedAccessModalOpened: true,
            });
        };
        this.closeRestrictedAccessModal = () => {
            this.setState({
                isRestrictedAccessModalOpened: false,
            });
        };
        this.getEffectiveStatus = () => {
            const { status = APPROVAL_STATUSES.NOT_SUBMITTED } = this.props;
            const { localStatus } = this.state;
            return status === APPROVAL_STATUSES.NOT_SUBMITTED ? localStatus : status;
        };
        this.getDescriptionHtml = () => {
            const { item } = this.props;
            if (!item.description) {
                return '';
            }
            const description = {
                ...emptyContent,
                ...item.description,
            };
            const editorState = EditorState.createWithContent(convertFromRaw(description));
            return stateToHTML(editorState.getCurrentContent());
        };
        this.openConfirmCompletionDialog = () => {
            this.setState({
                completionError: '',
                isConfirmCompletionDialogOpened: true,
            });
        };
        this.closeConfirmCompletionDialog = () => {
            const { isSubmitting = false } = this.props;
            const { isSubmittingCompletion } = this.state;
            if (!isSubmitting && !isSubmittingCompletion) {
                this.setState({
                    isConfirmCompletionDialogOpened: false,
                });
            }
        };
        this.confirmCompletion = async () => {
            const { item, course, getCourseBySlug, intl: { formatMessage }, } = this.props;
            this.setState({
                completionError: '',
                isSubmittingCompletion: true,
            });
            try {
                const params = {
                    courseId: course._id,
                };
                const query = buildQuery(params);
                const res = await this.context.fetch(`/api/approval-steps/${item._id}/approval-step-results-student?${query}`, {
                    method: 'POST',
                });
                if (!res.ok) {
                    const resJson = await res.json();
                    throw new Error(resJson?.message || formatMessage(messages.completionError));
                }
                await getCourseBySlug(course.slug);
                this.setState({
                    isConfirmCompletionDialogOpened: false,
                    localStatus: this.props.status || APPROVAL_STATUSES.NOT_SUBMITTED,
                });
            }
            catch (err) {
                console.error(err);
                this.setState({
                    completionError: err instanceof Error && err.message
                        ? err.message
                        : formatMessage(messages.completionError),
                });
            }
            finally {
                this.setState({
                    isSubmittingCompletion: false,
                });
            }
        };
    }
    render() {
        const { item, active, auth, intl: { formatMessage }, isCompleted, isSubmitting = false, offline, disabled, } = this.props;
        const { completionError, isConfirmCompletionDialogOpened, isRestrictedAccessModalOpened, isSubmittingCompletion, } = this.state;
        const effectiveStatus = this.getEffectiveStatus();
        const isWaitingForReview = effectiveStatus === APPROVAL_STATUSES.WAITING_FOR_REVIEW;
        const isApproved = effectiveStatus === APPROVAL_STATUSES.APPROVED;
        const isStepCompleted = Boolean(isCompleted || isApproved);
        const isButtonDisabled = offline ||
            disabled ||
            isSubmitting ||
            isSubmittingCompletion ||
            isWaitingForReview ||
            isApproved;
        const showCompleteButton = !isStepCompleted;
        const descriptionHtml = this.getDescriptionHtml();
        let listItem = (React.createElement(ListItem, { "data-test-id": "ICbU7WVGsdauVeGkjMFqBx", className: classNames(s.quizListItem, active ? s.active : undefined, {
                [s.archived]: !!item.removed,
            }), component: "div", disabled: offline || disabled, id: getApprovalStepId(item) },
            React.createElement(ListItemIcon, { classes: { root: s.approvalStepIconRoot } },
                React.createElement(SkholeIconApprovalStep, { className: s.approvalStepIcon, color: active ? '#fff' : 'primary' })),
            React.createElement("div", { className: s.approvalStepBody },
                React.createElement("div", { className: s.approvalStepHeader },
                    React.createElement(Typography, { className: s.quizTitleTextPrimary, component: "div" }, item.name),
                    React.createElement("span", { className: classNames(s.approvalStepStatusPill, getStatusClassName(effectiveStatus)) }, formatMessage(messages[getStatusMessage(effectiveStatus)])),
                    item.hasMissingApprover && (React.createElement("span", { className: classNames(s.approvalStepStatusPill, s.missingApproverStatus) }, formatMessage(messages.missingApprover)))),
                React.createElement("div", { className: s.studentViewContent },
                    offline && (React.createElement("div", null, formatMessage(messages.notAvailableOffline))),
                    !!item.removed && React.createElement("div", null, formatMessage(messages.archived)),
                    descriptionHtml && (React.createElement("div", { className: s.studentViewSection },
                        React.createElement(Typography, { className: s.studentViewLabel, variant: "subtitle1" }, formatMessage(messages.instructions)),
                        React.createElement(NoSsr, null,
                            React.createElement(Interweave, { tagName: "div", className: s.studentViewDescription, content: descriptionHtml })))),
                    React.createElement("div", { className: s.approvalStepFooter },
                        item.dueDate && (React.createElement("div", { className: s.approvalStepDueDate },
                            React.createElement(DateRangeIcon, { className: s.approvalStepDueDateIcon }),
                            React.createElement("span", { className: s.approvalStepDueDateLabel },
                                formatMessage(messages.dueDate),
                                ":"),
                            React.createElement("span", { className: s.approvalStepDueDateValue }, moment(item.dueDate).format('YYYY-MM-DD')))),
                        showCompleteButton && (React.createElement(Tooltip, { title: formatMessage(messages.markAsCompletedTooltip) },
                            React.createElement("span", { className: s.studentViewButtonWrapper },
                                React.createElement(Button, { "data-test-id": "BrWEmfJsnMKy4fMsHARWU", variant: "contained", color: "primary", disabled: isButtonDisabled, onClick: this.checkAuth },
                                    (isSubmitting || isSubmittingCompletion) && (React.createElement(CircularProgress, { size: 20, className: s.studentViewButtonProgress })),
                                    formatMessage(messages.markAsCompleted)))))))),
            isStepCompleted && isLoggedIn(auth) && (React.createElement(ListItemSecondaryAction, { className: s.secondaryAction },
                React.createElement(Tooltip, { id: "tooltip-approval-step-completed", title: formatMessage(messages.completed) },
                    React.createElement("div", { className: s.completedIcon }))))));
        if (offline) {
            listItem = (React.createElement(Tooltip, { title: formatMessage(messages.notAvailableOffline) },
                React.createElement("div", null, listItem)));
        }
        return (React.createElement("div", null,
            listItem,
            React.createElement(Modal, { open: isRestrictedAccessModalOpened, onClose: this.closeRestrictedAccessModal },
                React.createElement(RestrictedAccessModal, { onClose: this.closeRestrictedAccessModal })),
            React.createElement(Dialog, { open: isConfirmCompletionDialogOpened, onClose: this.closeConfirmCompletionDialog },
                React.createElement(DialogTitle, null, formatMessage(messages.confirmCompletion)),
                React.createElement(DialogContent, null,
                    descriptionHtml && (React.createElement("div", { className: s.confirmCompletionDescription },
                        React.createElement(NoSsr, null,
                            React.createElement(Interweave, { tagName: "div", className: s.studentViewDescription, content: descriptionHtml })))),
                    React.createElement(DialogContentText, null, formatMessage(messages.confirmCompletionDescription)),
                    completionError && (React.createElement(Typography, { color: "error" }, completionError))),
                React.createElement(DialogActions, null,
                    React.createElement(Button, { "data-test-id": "f9_G1nMoid9Ib_IulHrOZ", color: "primary", disabled: isSubmitting || isSubmittingCompletion, onClick: this.closeConfirmCompletionDialog }, formatMessage(messages.cancel)),
                    React.createElement(Button, { "data-test-id": "xGhfadVCfdTN-mwB6Svim", autoFocus: true, color: "primary", disabled: isSubmitting || isSubmittingCompletion, onClick: this.confirmCompletion, variant: "contained" }, formatMessage(messages.confirm))))));
    }
}
ApprovalStepListItem.defaultProps = {
    active: false,
    isCompleted: undefined,
    disabled: false,
    status: APPROVAL_STATUSES.NOT_SUBMITTED,
    isSubmitting: false,
};
ApprovalStepListItem.contextType = AppContext;
const mapStateToProps = (state) => ({
    auth: state.auth,
    lang: state.intl.locale,
    offline: state.online.offline,
});
const mapDispatchToProps = (dispatch) => bindActionCreators({
    getCourseBySlug: courseDetailsActions.getCourseBySlug,
}, dispatch);
export default compose(injectIntl, connect(mapStateToProps, mapDispatchToProps), withStyles(s))(ApprovalStepListItem);
